import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Tooltip, Empty } from 'antd';
import { ArrowLeftOutlined, WalletOutlined, DollarOutlined, CopyOutlined, LinkOutlined, InfoCircleOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};


const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
`;


const ExplorerIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 6px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const ExplorerLink = styled.a`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  color: ${COLORS.accent};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ChainCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 16px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid ${COLORS.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const BalanceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const BalanceAmounts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const CurrencyIcon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const ChainIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
`;

const WalletDetails: React.FC = () => {
  const { walletId } = useParams<{ walletId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState<any>(null);
  const [onchainBalances, setOnchainBalances] = useState<any>(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [blockchainNetworks, setBlockchainNetworks] = useState<any[]>([]);
  const [syncingToWatcher, setSyncingToWatcher] = useState(false);

  const fetchWalletDetails = useCallback(async () => {
    if (!walletId) return;
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/Administrator/wallets/${walletId}`);
      if (response.data?.status === 'success' && response.data?.data) {
        setWalletData(response.data.data);
      }
    } catch (error: any) {
      message.error('Failed to load wallet details');
    } finally {
      setLoading(false);
    }
  }, [walletId]);

  const fetchOnchainBalances = useCallback(async () => {
    if (!walletId) return;
    try {
      setLoadingBalances(true);
      const response = await apiClient.get((API_ENDPOINTS.ADMIN_GET_WALLET_ONCHAIN_BALANCES || '/api/Administrator/wallets/{walletId}/on-chain-wallet-balances').replace('{walletId}', walletId));
      if (response.data?.status === 'success' && response.data?.data) {
        setOnchainBalances(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching onchain balances:', error);
      message.error('Failed to load wallet balances');
    } finally {
      setLoadingBalances(false);
    }
  }, [walletId]);

  const fetchBlockchainNetworks = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS}?status=ACTIVE&limit=100`);
      if (response.data?.status === 'success' && response.data?.data?.itemsList) {
        setBlockchainNetworks(response.data.data.itemsList);
      }
    } catch (error: any) {
      console.error('Error fetching blockchain networks:', error);
    }
  }, []);

  const handleSyncToWatcher = useCallback(async () => {
    if (!walletId) return;
    
    if (!walletData?.externalMemberId) {
      message.warning('Wallet must be assigned to a member before syncing to watcher');
      return;
    }

    try {
      setSyncingToWatcher(true);
      const response = await apiClient.post(
        (API_ENDPOINTS.ADMIN_SYNC_WALLET_TO_WATCHER || '/api/Administrator/wallets/{walletId}/sync-to-watcher').replace('{walletId}', walletId)
      );
      
      if (response.data?.status === 'success') {
        message.success('Wallet synced to OnchainWatcher successfully');
        // Refresh wallet data to get updated watcherAcknowledged
        await fetchWalletDetails();
      } else {
        message.error(response.data?.message || 'Failed to sync wallet to watcher');
      }
    } catch (error: any) {
      console.error('Error syncing wallet to watcher:', error);
      message.error(error.response?.data?.message || 'Failed to sync wallet to watcher');
    } finally {
      setSyncingToWatcher(false);
    }
  }, [walletId, walletData, fetchWalletDetails]);

  useEffect(() => {
    if (walletId) {
      fetchWalletDetails();
      fetchOnchainBalances();
      fetchBlockchainNetworks();
    }
  }, [walletId, fetchWalletDetails, fetchOnchainBalances, fetchBlockchainNetworks]);

  const getEVMExplorerUrl = (network: any, address: string) => {
    if (!network?.explorerUrl || !address) return null;
    // Ensure explorer URL has https:// prefix
    const baseUrl = network.explorerUrl.startsWith('http') 
      ? network.explorerUrl 
      : `https://${network.explorerUrl}`;
    return `${baseUrl}/address/${address}`;
  };

  const getBitcoinExplorerUrl = (address: string) => {
    if (!address) return null;
    return `https://blockstream.info/address/${address}`;
  };

  const getSolanaExplorerUrl = (address: string) => {
    if (!address) return null;
    return `https://solscan.io/account/${address}`;
  };

  const getTronExplorerUrl = (address: string) => {
    if (!address) return null;
    return `https://tronscan.org/#/address/${address}`;
  };

  const renderEVMExplorerIcons = (address: string) => {
    if (!address) return null;
    
    const evmNetworks = blockchainNetworks.filter(
      (network) => network.blockchainType === 'EVM' && network.explorerUrl && network.status === 'ACTIVE'
    );

    if (evmNetworks.length === 0) return null;

    return (
      <Space size={4} style={{ marginLeft: 8 }}>
        {evmNetworks.map((network) => {
          const explorerUrl = getEVMExplorerUrl(network, address);
          if (!explorerUrl) return null;

          return (
            <Tooltip key={network.id} title={`View on ${network.name}`}>
              <ExplorerLink
                href={explorerUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {network.iconUrl ? (
                  <ExplorerIcon src={network.iconUrl} alt={network.name} />
                ) : (
                  <LinkOutlined style={{ fontSize: 14 }} />
                )}
              </ExplorerLink>
            </Tooltip>
          );
        })}
      </Space>
    );
  };

  const renderBitcoinExplorerIcon = (address: string) => {
    if (!address) return null;
    const explorerUrl = getBitcoinExplorerUrl(address);
    if (!explorerUrl) return null;

    return (
      <Tooltip title="View on Blockstream">
        <ExplorerLink
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <LinkOutlined style={{ fontSize: 14 }} />
        </ExplorerLink>
      </Tooltip>
    );
  };

  const renderSolanaExplorerIcon = (address: string) => {
    if (!address) return null;
    const explorerUrl = getSolanaExplorerUrl(address);
    if (!explorerUrl) return null;

    return (
      <Tooltip title="View on Solscan">
        <ExplorerLink
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <LinkOutlined style={{ fontSize: 14 }} />
        </ExplorerLink>
      </Tooltip>
    );
  };

  const renderTronExplorerIcon = (address: string) => {
    if (!address) return null;
    const explorerUrl = getTronExplorerUrl(address);
    if (!explorerUrl) return null;

    return (
      <Tooltip title="View on Tronscan">
        <ExplorerLink
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <LinkOutlined style={{ fontSize: 14 }} />
        </ExplorerLink>
      </Tooltip>
    );
  };

  const handlePushToSweep = () => {
    if (walletData?.walletId) {
      navigate(`/kivu/sweep-list?walletId=${encodeURIComponent(walletData.walletId)}`);
    } else {
      message.error('Wallet ID not available');
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!walletData) {
    return (
      <PageContainer>
        <Card>
          <Typography.Text>Wallet not found</Typography.Text>
        </Card>
      </PageContainer>
    );
  }


  return (
    <PageContainer>
      <HeaderContainer>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/kivu/wallets')}
        >
          Back to Wallets
        </Button>
        <Button
          type="primary"
          icon={<DollarOutlined />}
          onClick={handlePushToSweep}
        >
          Push to Sweep List
        </Button>
      </HeaderContainer>

      <StyledCard title="Wallet Information">
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Wallet ID">
            {walletData.walletId ? (
              <Space>
                <span style={{ fontFamily: 'monospace' }}>{walletData.walletId}</span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(walletData.walletId, 'Wallet ID')}
                  style={{ color: COLORS.accent }}
                />
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="EVM Address">
            {walletData.evmAddress ? (
              <Space>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {walletData.evmAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(walletData.evmAddress, 'EVM Address')}
                  style={{ color: COLORS.accent }}
                />
                {renderEVMExplorerIcons(walletData.evmAddress)}
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Bitcoin Address">
            {walletData.bitcoinAddress ? (
              <Space>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {walletData.bitcoinAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(walletData.bitcoinAddress, 'Bitcoin Address')}
                  style={{ color: COLORS.accent }}
                />
                {renderBitcoinExplorerIcon(walletData.bitcoinAddress)}
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Solana Address">
            {walletData.solanaAddress ? (
              <Space>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {walletData.solanaAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(walletData.solanaAddress, 'Solana Address')}
                  style={{ color: COLORS.accent }}
                />
                {renderSolanaExplorerIcon(walletData.solanaAddress)}
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="TRON Address">
            {walletData.tronAddress ? (
              <Space>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {walletData.tronAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(walletData.tronAddress, 'TRON Address')}
                  style={{ color: COLORS.accent }}
                />
                {renderTronExplorerIcon(walletData.tronAddress)}
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Merchant Company">
            {walletData.company?.name ? (
              <a
                href={`/kivu/companies/${walletData.company.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/kivu/companies/${walletData.company.id}`);
                }}
                style={{ color: COLORS.accent }}
              >
                {walletData.company.name}
              </a>
            ) : walletData.merchantName ? (
              walletData.merchantName
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Assigned Member ID">
            {walletData.externalMemberId ? (
              <span style={{ fontFamily: 'monospace' }}>{walletData.externalMemberId}</span>
            ) : (
              <Tag color="default">Not Assigned</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Assigned Member Name">
            {walletData.externalMemberName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Assigned Member Email">
            {walletData.externalMemberEmail || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Wallet Status">
            <Tag color={walletData.walletStatus === 'ACTIVE' ? 'green' : 'orange'}>
              {walletData.walletStatus}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item 
            label={
              <Space>
                <span>Notional Balance</span>
                <Tooltip title="This value is a currency-agnostic sum—essentially a mixed-currency total used as a placeholder total because the amounts were added without converting them into a single currency. When it is 0 it means no funds are in the wallet on any chain. OnchainWatcher will add to it the sum, and request from Aso rock will deduct from it when a sweep (remittance) happen. When it is negative, it means we have pending jobs from OnchainWatcher that have not received callback from, or it has not scanned the chain for a transaction but sweep (remittance) already happened.">
                  <InfoCircleOutlined style={{ color: COLORS.accent, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
          >
            <span 
              style={{ 
                fontFamily: 'monospace', 
                fontWeight: 500,
                color: Number(walletData.notionalBalance || 0) < 0 ? '#F87171' : Number(walletData.notionalBalance || 0) > 0 ? '#10B981' : COLORS.textPrimary
              }}
            >
              {Number(walletData.notionalBalance || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 18,
              })}
            </span>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space>
                <span>Under Watch</span>
                <Tooltip title="Indicates if this wallet has been registered with OnchainWatcher for on-chain transaction monitoring. When enabled, the system monitors this wallet for incoming transactions and balance changes across supported blockchains.">
                  <InfoCircleOutlined style={{ color: COLORS.accent, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space>
                <Tag color={walletData.isUnderWatch ? 'green' : 'default'}>
                  {walletData.isUnderWatch ? 'Yes' : 'No'}
                </Tag>
                {walletData.externalMemberId && walletData.externalMemberName !== 'GasWallet' && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={handleSyncToWatcher}
                    loading={syncingToWatcher}
                    disabled={syncingToWatcher}
                    style={{
                      background: COLORS.accent,
                      borderColor: COLORS.accent,
                    }}
                  >
                    {walletData.isUnderWatch ? 'Resync to Watcher' : 'Sync to Watcher'}
                  </Button>
                )}
              </Space>
              {walletData.isUnderWatch && walletData.watcherAcknowledged && (
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  <span style={{ fontWeight: 500 }}>Watcher ID:</span>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{walletData.watcherAcknowledged}</span>
                </div>
              )}
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      {/* Wallet On-Chain Balances */}
      <StyledCard
        title={
          <Space>
            <WalletOutlined />
            <span>Wallet On-Chain Balances</span>
            {onchainBalances && (
              <Tag color="green">
                Total: ${Number(onchainBalances.totalUsd || 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Tag>
            )}
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchOnchainBalances}
              loading={loadingBalances}
              style={{ color: COLORS.accent }}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {loadingBalances && !onchainBalances ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: COLORS.textSecondary }}>
              Loading wallet balances...
            </div>
          </div>
        ) : onchainBalances && onchainBalances.chains && onchainBalances.chains.length > 0 ? (
          onchainBalances.chains.map((chain: any) => (
            <ChainCard
              key={chain.chainId}
              title={
                <Space>
                  {chain.chainIconUrl && (
                    <ChainIcon src={chain.chainIconUrl} alt={chain.chainName} />
                  )}
                  <span>{chain.chainName}</span>
                  <Tag color="blue">
                    ${Number(chain.totalUsd || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Tag>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              {chain.balances && chain.balances.length > 0 ? (
                chain.balances.map((balance: any, index: number) => (
                  <BalanceRow key={index}>
                    <BalanceInfo>
                      {balance.currencyIconUrl && (
                        <CurrencyIcon src={balance.currencyIconUrl} alt={balance.currencySymbol} />
                      )}
                      {balance.blockchainNetworkIconUrl && !balance.currencyIconUrl && (
                        <CurrencyIcon src={balance.blockchainNetworkIconUrl} alt={balance.blockchainNetworkName} />
                      )}
                      <div>
                        <div style={{ fontWeight: 500, color: COLORS.textPrimary }}>
                          {balance.currencyName || balance.currencySymbol}
                        </div>
                        {balance.contractAddress && (
                          <div style={{ fontSize: '11px', color: COLORS.textSecondary, fontFamily: 'monospace' }}>
                            {balance.contractAddress.substring(0, 10)}...{balance.contractAddress.substring(balance.contractAddress.length - 8)}
                          </div>
                        )}
                        {balance.error && (
                          <div style={{ marginTop: 4 }}>
                            <Tag color="red" icon={<WarningOutlined />}>
                              {balance.error}
                            </Tag>
                          </div>
                        )}
                        {balance.rpcFailed && (
                          <div style={{ marginTop: 4 }}>
                            <Tag color="orange">
                              RPC Failed: {balance.rpcUsed || 'All RPCs failed'}
                            </Tag>
                          </div>
                        )}
                      </div>
                    </BalanceInfo>
                    <BalanceAmounts>
                      <div style={{ fontWeight: 600, fontSize: '16px', color: COLORS.textPrimary }}>
                        {Number(balance.balance || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 8,
                        })}{' '}
                        {balance.currencySymbol}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        ${Number(balance.balanceUsd || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </BalanceAmounts>
                  </BalanceRow>
                ))
              ) : (
                <Empty
                  description="No balances found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ color: COLORS.textSecondary }}
                />
              )}
            </ChainCard>
          ))
        ) : (
          <Empty
            description="No wallet balances available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ color: COLORS.textSecondary }}
          />
        )}
      </StyledCard>
    </PageContainer>
  );
};

export default WalletDetails;

